import { Col, Row } from 'react-bootstrap';
import React, { useState } from 'react';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import * as Yup from 'yup';
import { connect, ResolveThunks } from 'react-redux';
import FormField from '../../../form/FormField';
import { FormikState } from 'formik/dist/types';
import { showFeedback } from '../../../feedback/feedbackReducer';
import {
  CAN_ADD_SCREENING_CLEARANCE_RULE_CONTENT,
  CAN_MODIFY_SCREENING_CLEARANCE_RULE_CONTENT,
} from '../../../auth/permissions';
import Has from '../../../auth/Has';
import { SalvButton, SalvModal } from '@salvapp/ui-components';
import {
  InternalApiScreeningClearanceRuleContent,
  InternalApiScreeningClearanceRuleContentStatusEnum,
} from '../../../api-internal';
import { saveScreeningClearanceRuleContent } from './screeningClearanceRuleContentActions';
import { parseErrorMessage } from '../../../common/errors/helpers';

const validationSchema = Yup.object().shape(
  {
    id: Yup.number(),
    content: Yup.string().required('Required'),
    note: Yup.string().required('Required'),
  },
  []
);

export interface ScreeningClearanceRuleContentModalFormValues {
  id?: number;
  ruleHandle: string;
  note?: string;
  content?: string;
  status: {
    label: string;
    value: InternalApiScreeningClearanceRuleContentStatusEnum;
  };
}

interface RuleModalProps {
  ruleHandle: string;
  content: InternalApiScreeningClearanceRuleContent;
  show: boolean;
  onComplete: (content?: InternalApiScreeningClearanceRuleContent) => void;
}

export const ScreeningClearanceRuleContentModal = ({
  saveScreeningClearanceRuleContent,
  showError,
  content,
  ruleHandle,
  show,
  onComplete,
}: Props) => {
  const [disabled] = useState(
    content !== null && content.status !== InternalApiScreeningClearanceRuleContentStatusEnum.DRAFT
  );
  const initialValues: ScreeningClearanceRuleContentModalFormValues =
    content !== null
      ? {
          id: content.id,
          ruleHandle: ruleHandle,
          content: content.content,
          note: content.note,
          status: { label: content.status.toString(), value: content.status },
        }
      : {
          ruleHandle: ruleHandle,
          content: '',
          note: '',
          status: {
            label: InternalApiScreeningClearanceRuleContentStatusEnum.DRAFT.toString(),
            value: InternalApiScreeningClearanceRuleContentStatusEnum.DRAFT,
          },
        };

  const initialFormikState: Partial<FormikState<ScreeningClearanceRuleContentModalFormValues>> = {
    values: initialValues,
    isSubmitting: false,
  };

  const submit = async (
    values: ScreeningClearanceRuleContentModalFormValues,
    { setStatus, setSubmitting, resetForm }: FormikHelpers<ScreeningClearanceRuleContentModalFormValues>
  ) => {
    try {
      await saveScreeningClearanceRuleContent(values);
      resetForm(initialFormikState);
      onComplete();
    } catch (e: any) {
      const errorMessage = await parseErrorMessage(e);
      setStatus(errorMessage);
      showError(errorMessage);
    }
    setSubmitting(false);
  };

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      onSubmit={submit}
      validationSchema={validationSchema}
    >
      {({ isSubmitting, resetForm }: FormikProps<ScreeningClearanceRuleContentModalFormValues>) => {
        const closeModal = () => {
          resetForm(initialFormikState);
          onComplete();
        };
        return (
          <SalvModal show={show} onHide={closeModal}>
            <Form>
              <SalvModal.Header closeButton>
                <Has
                  anyPermissions={[
                    CAN_ADD_SCREENING_CLEARANCE_RULE_CONTENT,
                    CAN_MODIFY_SCREENING_CLEARANCE_RULE_CONTENT,
                  ]}
                >
                  {(hasPermission: boolean) => {
                    if (hasPermission) {
                      if (disabled) {
                        return 'View clearance rule version';
                      }
                      return initialValues.id ? 'Edit clearance rule version' : 'Add clearance rule version';
                    } else {
                      return 'View clearance rule version';
                    }
                  }}
                </Has>
              </SalvModal.Header>
              <SalvModal.Body>
                <Row>
                  <Col md={12}>
                    <FormField
                      disabled={disabled}
                      name='content'
                      type='textarea'
                      label='Content'
                      text='Rule that will be executed'
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <FormField
                      name='note'
                      type='textarea'
                      label='Note'
                      disabled={disabled}
                      text='Comment on what is changing in this version.'
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={5}>
                    <FormField
                      name='status'
                      label='Status'
                      placeholder='Select...'
                      type='select'
                      disabled={disabled}
                      options={Object.entries(InternalApiScreeningClearanceRuleContentStatusEnum)
                        .filter(([key]) => key !== InternalApiScreeningClearanceRuleContentStatusEnum.HISTORIC)
                        .map(([key, value]) => ({
                          key: key,
                          value: value,
                          label: value,
                        }))}
                    />
                  </Col>
                </Row>
              </SalvModal.Body>
              <Has
                anyPermissions={[CAN_ADD_SCREENING_CLEARANCE_RULE_CONTENT, CAN_MODIFY_SCREENING_CLEARANCE_RULE_CONTENT]}
              >
                {!disabled && (
                  <SalvModal.Footer>
                    <SalvButton disabled={isSubmitting} type='submit' variant='primary' label='Save' />
                  </SalvModal.Footer>
                )}
              </Has>
            </Form>
          </SalvModal>
        );
      }}
    </Formik>
  );
};

const mapStateToProps = () => {
  return {};
};

const mapDispatchToProps = {
  saveScreeningClearanceRuleContent,
  showError: (error: string) =>
    showFeedback({
      message: error,
      success: false,
    }),
};

type Props = RuleModalProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export default connect(mapStateToProps, mapDispatchToProps)(ScreeningClearanceRuleContentModal);
